import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { LoadingSpinner, ValidationError } from '@gik/ui';
import { useSituationsById } from '@gik/api/situation/situationsById';
import { useGeneralPageNameSuggestions } from '@gik/api/acf/gik-settings/generalPageNameSuggestions';
import withSuspense from '@gik/core/components/suspended/withSuspense';

export interface IPageNameIdeasProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  onSelect?: (name: string) => void;
  situationIds: number[];
}

const blockName = 'page-name-ideas';

export interface ICreateAboutFormValues {
  recipientType: string;
  email: string;
}

export interface ICreateAboutFormErrors {
  recipientType?: ValidationError;
  email?: ValidationError;
}

export const formId = 'CreateNameYourPageStepForm';

function Skeleton(props: Partial<IPageNameIdeasProps>) {
  const bem = bemBlock('page-name-ideas-skeleton');
  return (
    <div className={bem(null, ['loading'], props.className)}>
      <LoadingSpinner center />
    </div>
  );
}

function PageNameIdeas({ className, situationIds, onSelect, ...otherProps }: IPageNameIdeasProps) {
  const bem = bemBlock(blockName);

  const { data: situations, error: situationsError } = useSituationsById(situationIds);
  const { data: generalSuggestions, error: generalSuggestionsError } = useGeneralPageNameSuggestions();

  // eslint-disable-next-line no-constant-condition
  if (!situations || !generalSuggestions) {
    return <Skeleton className={className} />;
  }

  const isLoading = !situations || !generalSuggestions;
  const isError = isLoading && (situationsError || generalSuggestionsError);

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  return (
    <div className={bem(null, null, className)} {...otherProps}>
      <ul className={bem('list')}>
        {generalSuggestions.page_name_suggestions.map(item => (
          <li key={item.suggestion} onClick={() => onSelect(item.suggestion)}>
            {item.suggestion}
          </li>
        ))}
      </ul>

      {situationIds?.map(id => {
        const situation = situations.find(item => item.id === id);
        const pageNameSuggestions = situation.acf.page_name_suggestions?.map(item => item.suggestion);

        return (
          <div key={id}>
            <ul className={bem('list')}>
              {pageNameSuggestions?.map(suggestion => (
                <li key={suggestion} onClick={() => onSelect(suggestion)}>
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export default withSuspense(PageNameIdeas, Skeleton);
